import { Component, createRef } from "react"

/* Components */
import { DataLayout, Icon } from "../../components"

/* Widgets */
import { PromoForm, PromoModal } from "../../widgets/promo"
import { toast, confirm } from "../../widgets/common"

/* Shared */
import { STATES, TABS, stateOf, monthsText, usesText, untilText } from "../../widgets/promo/shared"
import { ago } from "../../widgets/user/shared"
import { plural } from "../../widgets/category/shared"

/* Helpers */
import { withRouter } from "../../helpers"

/* REST API */
import { list, status, remove } from "../../api/Promo"


/* Page Promos — the promo codes of the platform. A point opened in Bookify Pro with a code gets its
   first months of the subscription for free. There are a handful of codes, so the server gives the
   list whole: the search goes to the server, the tabs of the states filter right on the page and
   keep their counters */
class Promos extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            counters: { codes: 0, active: 0, uses: 0, month: 0, months: 0 },

            /* "all" or one of the states */
            tab: "all",
            search: ""
        }

        this._form = createRef()
        this._promo = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(true)
    }

    componentWillUnmount = () => {

        if (this._timer) {
            clearTimeout(this._timer)
        }
    }

    load = (reload = false) => {

        /* Fields */
        const { search } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        list({ search }).then(response => {
            if (response && response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, counters: response.data.counters })
            }
            else {
                this.setState({ status: "error", data: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [] })
        })
    }

    /* The search waits until the typing stops */
    search = value => {

        this.setState({ search: value })

        if (this._timer) {
            clearTimeout(this._timer)
        }

        this._timer = setTimeout(() => this.load(), 350)
    }

    /* Switch a code on or off right from its card */
    change = (event, item) => {

        event.stopPropagation()

        const value = !item.active

        status({ id: item.id, active: value }).then(response => {

            if (response && response.status === 200) {
                toast.success(value ? "Промокод включён" : "Промокод выключен")
                this.load()
            }
            else {
                toast.error("Что-то пошло не так! Попробуйте позднее")
            }
        }).catch(() => {
            toast.error("Что-то пошло не так! Попробуйте позднее")
        })
    }

    remove = (event, item) => {

        event.stopPropagation()

        let text = "Его ещё никто не вводил. Точки больше не смогут ввести этот код"

        if (item.usedCount > 0) {
            text = `Точки больше не смогут ввести этот код. ${item.usedCount} ${plural(item.usedCount, "точка", "точки", "точек")}, которые уже ввели его, сохранят свои бесплатные месяцы`
        }

        confirm({
            title: `Удалить промокод ${item.code}?`,
            text,
            confirmText: "Да, удалить",
            cancelText: "Нет",
            danger: true
        }).then(confirmed => {

            if (!confirmed) {
                return
            }

            remove({ id: item.id }).then(response => {

                if (response && response.status === 200) {
                    toast.success("Промокод удалён")
                    this.load()
                    return
                }

                toast.error("Что-то пошло не так! Попробуйте позднее")
            }).catch(() => toast.error("Что-то пошло не так! Попробуйте позднее"))
        })
    }

    copy = (event, text) => {

        event.stopPropagation()

        if (!navigator.clipboard || !text) {
            return
        }

        navigator.clipboard.writeText(text).then(() => toast.success("Скопировано")).catch(() => { })
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Промокоды</div>

                <div className="organization-header-tools">

                    <form onSubmit={event => event.preventDefault()} className="organization-search">

                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>

                        <input placeholder="Код или описание" value={search} onChange={event => this.search(event.target.value)} />

                        {search.length > 0 &&
                            <div onClick={() => this.search("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }

                    </form>

                    <div onClick={() => this._form.current.open()} className="organization-create">
                        <Icon name="plus" size={17} width={2.4} />
                        <span>Добавить</span>
                    </div>

                </div>

            </div>
        )
    }

    /* The numbers of the platform */
    _summary = () => {

        /* Fields */
        const { counters } = this.state

        const cards = [
            { icon: "ticket", value: counters.codes, text: plural(counters.codes, "промокод", "промокода", "промокодов"), accent: "blue" },
            { icon: "check", value: counters.active, text: "включено", accent: "green" },
            { icon: "organization", value: counters.uses, text: `${plural(counters.uses, "точка открыта", "точки открыты", "точек открыто")} по коду`, accent: "violet" },
            { icon: "calendar", value: counters.month, text: "за 30 дней", accent: "orange" },
            { icon: "gift", value: counters.months, text: `${plural(counters.months, "месяц", "месяца", "месяцев")} подарено`, accent: "grey" }
        ]

        return (
            <div className="category-summary">
                {cards.map(card =>
                    <div className={`category-summary-item ${card.accent}`} key={card.icon}>
                        <div className="category-summary-icon">
                            <Icon name={card.icon} size={18} width={2} />
                        </div>
                        <div className="category-summary-text">
                            <b>{card.value}</b>
                            <span>{card.text}</span>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    /* The tabs of the states */
    _tabs = () => {

        /* Fields */
        const { tab, data } = this.state

        return (
            <div className="category-tabs">

                <div onClick={() => this.setState({ tab: "all" })} className={`category-tab ${tab === "all" ? "active" : ""}`}>
                    Все
                    <span>{data.length}</span>
                </div>

                {TABS.map(key =>
                    <div key={key} onClick={() => this.setState({ tab: key })} className={`category-tab promo-tab ${STATES[key].accent} ${tab === key ? "active" : ""}`} title={STATES[key].hint}>
                        <i />
                        {STATES[key].text}
                        <span>{data.filter(item => item.state === key).length}</span>
                    </div>
                )}

            </div>
        )
    }

    /* One code */
    _card = item => {

        const state = stateOf(item.state)

        return (
            <div key={`${item.id}`} onClick={() => this._promo.current.open(item)} className={`promo-card ${item.state}`}>

                <div className="promo-card-top">
                    <div onClick={event => this.copy(event, item.code)} className="promo-code" title="Скопировать">
                        <Icon name="ticket" size={16} width={2} />
                        {item.code}
                    </div>
                    <div onClick={event => this.change(event, item)} className={`category-card-toggle static ${item.active ? "on" : ""}`} title={item.active ? "Выключить" : "Включить"}>
                        <i />
                    </div>
                </div>

                <div className="promo-card-description">{item.description ? item.description : "Без описания"}</div>

                <div className="promo-card-badges">
                    <div className={`user-badge ${state.accent}`} title={state.hint}><i />{state.text}</div>
                    <div className="user-badge">{monthsText(item.months)} бесплатно</div>
                </div>

                <div className="admin-card-facts">
                    <div>
                        <span>Использован</span>
                        <b>{usesText(item)}</b>
                    </div>
                    <div>
                        <span>Срок</span>
                        <b>{untilText(item.validUntil)}</b>
                    </div>
                    <div>
                        <span>Последний раз</span>
                        <b>{item.lastUsedAt ? ago(item.lastUsedAt) : "ещё не вводили"}</b>
                    </div>
                </div>

                <div className="admin-card-actions">
                    <div onClick={event => { event.stopPropagation(); this._promo.current.open(item) }} className="admin-card-button">
                        <Icon name="organization" size={15} width={2} />
                        Точки
                    </div>
                    <div onClick={event => { event.stopPropagation(); this._form.current.open(item) }} className="admin-card-button">
                        <Icon name="edit" size={15} width={2} />
                        Изменить
                    </div>
                    <div onClick={event => this.remove(event, item)} className="admin-card-button danger" title="Удалить">
                        <Icon name="trash" size={15} width={2} />
                    </div>
                </div>

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data, tab, search } = this.state

        if (status !== "ok") {
            return (
                <div className="page-content">
                    <DataLayout status={status} reload={() => this.load(true)} data={data} />
                </div>
            )
        }

        let items = data

        if (tab !== "all") {
            items = data.filter(item => item.state === tab)
        }

        return (
            <div className="page-content">

                {this._summary()}

                {this._tabs()}

                {items.length === 0 && (search.length > 0 || tab !== "all") &&
                    <DataLayout status="ok" data={[]} empty={{ text: "Попробуйте ещё раз, изменив поиск или вкладку" }} onReset={() => this.setState({ tab: "all" }, () => this.search(""))} />
                }

                {(items.length > 0 || (search.length === 0 && tab === "all")) &&
                    <div className="admin-grid">

                        {items.map(item => this._card(item))}

                        {search.length === 0 && tab === "all" &&
                            <div onClick={() => this._form.current.open()} className="admin-card add promo-add">
                                <div className="admin-add-icon"><Icon name="plus" size={22} width={2.2} /></div>
                                <b>Новый промокод</b>
                                <span>Код и сколько месяцев он дарит точке</span>
                            </div>
                        }

                    </div>
                }

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { navigate } = this.props

        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <PromoForm ref={this._form} onSaved={() => this.load()} />

                <PromoModal
                    ref={this._promo}
                    onOrganization={id => navigate(`/organization?open=${id}`)}
                    onEdit={promo => this._form.current.open(promo)}
                />

            </div>
        )
    }
}

export default withRouter(Promos)
